import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from 'react';
import { clsx } from './clsx';

/**
 * Shared primitives so eight panels don't produce eight visual dialects of a
 * checkbox. All token-styled; panels never write a `dark:` class or a hex.
 */

export function Button({
  variant = 'default',
  className,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'default' | 'accent' | 'ghost' }) {
  return (
    <button
      type="button"
      {...rest}
      className={clsx(
        'shrink-0 rounded-lg px-2.5 py-1.5 text-xs font-medium transition-colors',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent',
        'disabled:cursor-not-allowed disabled:opacity-50',
        variant === 'accent' && 'bg-accent text-accent-ink hover:opacity-90',
        variant === 'default' && 'border border-line text-ink-muted hover:bg-panel-raised',
        variant === 'ghost' && 'text-ink-muted hover:bg-panel-raised',
        className,
      )}
    />
  );
}

export function IconButton({
  label,
  className,
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { label: string; children: ReactNode }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      {...rest}
      className={clsx(
        'grid h-6 w-6 shrink-0 place-items-center rounded text-ink-subtle transition-colors',
        'hover:bg-panel-raised hover:text-ink',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent',
        className,
      )}
    >
      {children}
    </button>
  );
}

export function TextInput({ className, ...rest }: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...rest}
      className={clsx(
        'min-w-0 rounded-lg border border-line bg-surface px-2.5 py-1.5',
        'text-[13px] text-ink placeholder:text-ink-subtle',
        'focus:border-accent focus:outline-none',
        className,
      )}
    />
  );
}

export function Checkbox({
  checked,
  onChange,
  label,
  className,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  className?: string;
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      className={clsx(
        'grid h-4 w-4 shrink-0 place-items-center rounded border transition-colors',
        checked ? 'border-accent bg-accent text-accent-ink' : 'border-line bg-surface',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent',
        className,
      )}
    >
      {checked && (
        <svg viewBox="0 0 12 12" className="h-3 w-3" aria-hidden>
          <path
            d="M2.5 6.2 4.8 8.5 9.5 3.8"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </button>
  );
}

export function Pill({
  tone = 'neutral',
  children,
  className,
  testId,
}: {
  tone?: 'neutral' | 'accent' | 'positive' | 'negative' | 'warn' | 'alert';
  children: ReactNode;
  className?: string;
  /**
   * Note: a literal `data-testid` prop would type-check on a component and
   * then be silently dropped — TypeScript skips excess-property checks for
   * hyphenated JSX attributes. Pass it through here instead.
   */
  testId?: string;
}) {
  return (
    <span
      data-testid={testId}
      className={clsx(
        'shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
        tone === 'neutral' && 'bg-panel-raised text-ink-subtle',
        tone === 'accent' && 'bg-accent-soft text-accent',
        tone === 'positive' && 'bg-panel-raised text-positive',
        tone === 'negative' && 'bg-panel-raised text-negative',
        tone === 'warn' && 'bg-panel-raised text-warn',
        tone === 'alert' && 'bg-alert text-accent-ink',
        className,
      )}
    >
      {children}
    </span>
  );
}
